import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PhoneForwarded, MessageSquare, Phone, Plus, Users, Calendar } from "lucide-react";
import { useDevicesStore } from '@/stores/devices-store';
import { useCustomerStore, CallHandlingProfile } from '@/stores/customer-store';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';
import { ProfileDialog } from '@/components/forward/profile-dialog';
import { DeleteProfileDialog } from '@/components/forward/delete-profile-dialog';

interface Receiver {
  id: string;
  name: string;
  phoneNumber: string;
  active: boolean;
  description?: string;
}

interface Schedule {
  id: string;
  name: string;
  receiverIds: string[];
  active: boolean;
  description?: string;
  days?: string[];
  timeFrom?: string;
  timeTo?: string;
}

export default function ForwardPage() {
  const { currentDevice, getAutomaticSmsConfiguration } = useDevicesStore();
  const { 
    callhandlingProfiles, 
    loadCallHandlingProfiles,
    createCallHandlingProfile,
    updateCallHandlingProfile,
    deleteCallHandlingProfile
  } = useCustomerStore();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState<'recipients' | 'schedules' | 'profiles'>('recipients');
  const [smsConfig, setSmsConfig] = useState<{ enabled: boolean; phoneNumber?: string; message?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [receivers, setReceivers] = useState<Receiver[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<CallHandlingProfile | null>(null);
  const [deletingProfile, setDeletingProfile] = useState<CallHandlingProfile | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (!currentDevice) {
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      try {
        const [config] = await Promise.all([
          getAutomaticSmsConfiguration(),
          loadCallHandlingProfiles(),
          loadReceivers(),
          loadSchedules()
        ]);
        setSmsConfig(config);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentDevice, getAutomaticSmsConfiguration, loadCallHandlingProfiles]);

  // Load receivers from API
  const loadReceivers = async () => {
    // No receivers in current API - keeping empty for now
    // This would be implemented when backend API is available
    setReceivers([]);
  };

  // Load schedules from API
  const loadSchedules = async () => {
    // No schedules in current API - keeping empty for now
    // This would be implemented when backend API is available
    setSchedules([]);
  };

  const handleCreateProfile = () => {
    setEditingProfile(null);
    setIsProfileDialogOpen(true);
  };

  const handleEditProfile = (profile: CallHandlingProfile) => {
    setEditingProfile(profile);
    setIsProfileDialogOpen(true);
  };

  const handleDeleteProfile = (profile: CallHandlingProfile) => {
    setDeletingProfile(profile);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveProfile = async (profile: Partial<CallHandlingProfile>) => {
    try {
      if (profile.id) {
        await updateCallHandlingProfile(profile as CallHandlingProfile);
        toast({
          title: "Profil aktualisiert",
          description: `Das Profil "${profile.name}" wurde aktualisiert.`
        });
      } else {
        await createCallHandlingProfile(profile);
        toast({
          title: "Profil erstellt",
          description: `Das Profil "${profile.name}" wurde erstellt.`
        });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Das Profil konnte nicht gespeichert werden.",
        variant: "destructive"
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingProfile) return;

    try {
      await deleteCallHandlingProfile(deletingProfile.id);
      toast({
        title: "Profil gelöscht",
        description: `Das Profil "${deletingProfile.name}" wurde gelöscht.`
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Das Profil konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingProfile(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Anrufweiterleitung
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Verwalten Sie Empfänger und Zeitpläne für die automatische Weiterleitung
              </p>
            </div>
            {/* Quick Stats */}
            <div className="hidden md:flex items-center gap-3">
              <div className="px-3 py-2 bg-brand-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-brand-primary rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">{receivers.length} Empfänger</span>
                </div>
              </div>
              <div className="px-3 py-2 bg-brand-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-brand-primary rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">{schedules.length} Zeitpläne</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
          {/* Tab Navigation */}
          <TabsList className="inline-flex bg-gray-100 rounded-md p-1 mb-6">
            <TabsTrigger value="recipients" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Empfänger
              {receivers.length > 0 && (
                <Badge variant="secondary" className="ml-1">{receivers.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="schedules" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Zeitpläne
              {schedules.length > 0 && (
                <Badge variant="secondary" className="ml-1">{schedules.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="profiles" className="flex items-center gap-2">
              <PhoneForwarded className="h-4 w-4" />
              Profile
              {callhandlingProfiles.length > 0 && (
                <Badge variant="secondary" className="ml-1">{callhandlingProfiles.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Recipients Tab */}
          <TabsContent value="recipients">
            <div className="grid gap-6 md:grid-cols-2">
              {/* SMS Configuration Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-gray-600" />
                      Automatische SMS
                    </span>
                    <Badge variant={smsConfig?.enabled ? 'default' : 'secondary'}>
                      {smsConfig?.enabled ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" />
                      <p className="text-sm text-gray-600 mt-2">Lade...</p>
                    </div>
                  ) : smsConfig ? (
                    <>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600">Status</span>
                        <Badge variant={smsConfig.enabled ? 'default' : 'secondary'}>
                          {smsConfig.enabled ? 'Aktiviert' : 'Deaktiviert'}
                        </Badge>
                      </div>
                      {smsConfig.phoneNumber && (
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm text-gray-600">Telefonnummer</span>
                          <span className="text-sm font-mono">{smsConfig.phoneNumber}</span>
                        </div>
                      )}
                      {smsConfig.message && (
                        <div className="py-2">
                          <span className="text-sm text-gray-600 block mb-2">Nachricht</span>
                          <p className="text-sm bg-gray-50 p-3 rounded-lg">{smsConfig.message}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Keine Konfiguration</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Receivers List Card */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-gray-600" />
                      Empfänger
                    </span>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Neuer Empfänger
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {receivers.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 font-medium">Keine Empfänger</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Fügen Sie Ihren ersten Empfänger hinzu
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {receivers.map((receiver) => (
                        <div
                          key={receiver.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className="font-semibold text-gray-900">{receiver.name}</h3>
                              <Badge variant={receiver.active ? 'default' : 'secondary'}>
                                {receiver.active ? 'Aktiv' : 'Inaktiv'}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              <Phone className="h-3 w-3 inline mr-1" />
                              {receiver.phoneNumber}
                            </p>
                            {receiver.description && (
                              <p className="text-sm text-gray-500 mt-1">{receiver.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm">
                              Bearbeiten
                            </Button>
                            <Button variant="outline" size="sm">
                              Löschen
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Schedules Tab */}
          <TabsContent value="schedules">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-gray-600" />
                    Zeitpläne
                  </span>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Neuer Zeitplan
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {schedules.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Keine Zeitpläne</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Erstellen Sie automatische Weiterleitungsregeln
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {schedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-gray-900">{schedule.name}</h3>
                            <Badge variant={schedule.active ? 'default' : 'secondary'}>
                              {schedule.active ? 'Aktiv' : 'Inaktiv'}
                            </Badge>
                          </div>
                          {schedule.description && (
                            <p className="text-sm text-gray-600 mt-1">{schedule.description}</p>
                          )}
                          {schedule.days && (
                            <p className="text-xs text-gray-500 mt-1">
                              {schedule.days.join(', ')} • {schedule.timeFrom} - {schedule.timeTo}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">
                            Bearbeiten
                          </Button>
                          <Button variant="outline" size="sm">
                            Löschen
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profiles Tab */}
          <TabsContent value="profiles">

            <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <PhoneForwarded className="h-5 w-5 text-gray-600" />
                  Weiterleitungs-Profile
                </span>
                <Button onClick={handleCreateProfile}>
                  <Plus className="h-4 w-4 mr-2" />
                  Neues Profil
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" />
                  <p className="text-sm text-gray-600 mt-2">Lade Profile...</p>
                </div>
              ) : callhandlingProfiles.length === 0 ? (
                <div className="text-center py-12">
                  <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">Keine Profile</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Erstelle dein erstes Weiterleitungsprofil
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {callhandlingProfiles.map((profile) => (
                    <div
                      key={profile.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-gray-900">{profile.name}</h3>
                          <Badge variant={profile.active ? 'default' : 'secondary'}>
                            {profile.active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </div>
                        {profile.phoneNumber && (
                          <p className="text-sm text-gray-600 mt-1">
                            <Phone className="h-3 w-3 inline mr-1" />
                            {profile.phoneNumber}
                          </p>
                        )}
                        {profile.description && (
                          <p className="text-sm text-gray-500 mt-1">{profile.description}</p>
                        )}
                        {profile.usedByActions && profile.usedByActions.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            Verwendet von {profile.usedByActions.length} Antwort(en)
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditProfile(profile)}
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteProfile(profile)}
                        >
                          Löschen
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <ProfileDialog
        isOpen={isProfileDialogOpen}
        onClose={() => setIsProfileDialogOpen(false)}
        onSave={handleSaveProfile}
        profile={editingProfile}
      />
      <DeleteProfileDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        profileName={deletingProfile?.name || ''}
      />
    </div>
  );
}
